import React, { useEffect, useState } from "react";
import "font-awesome/css/font-awesome.min.css";
import ApartmentFormDialog from "./ApartmentFormDialog";
import { Button, Grid, IconButton } from "@mui/material";
import { Add, AttachFile, Delete, Edit } from "@mui/icons-material";
import { useForm, useFormContext } from "react-hook-form";
import FilePreviewer from "qubCommon/FilePreviewer";
import { useYupValidationResolver } from "hooks/useYupValidationResolver";
import { useApartmentValidation } from "util/validations/apartment-form";
import { deleteFile, getFiles, uploadFiles } from "apis/controllers/files";
import { v4 as uuidv4 } from "uuid";
import { downloadFile } from "util";
import { useLookup } from "context/lookupContext";

const ApartmentsTable = ({
  apartments,
  branch,
  canEdit,
  canAdd,
  canDelete,
  canPartiallyEdit,
}) => {
  const [data, setData] = useState(apartments);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);

  const apartmentValidation = useApartmentValidation();
  const resolver = useYupValidationResolver(apartmentValidation);
  const apartmentForm = useForm({ resolver });
  const form = useFormContext();
  const { lookups } = useLookup();

  const handleCloseDialog = () => {
    apartmentForm.reset({
      municipalityId: null,
      prefectureId: null,
      regionId: null,
    });
    setOpenDialog(false);
  };

  const fetchFiles = async () => {
    if (data?.length > 0) {
      const dataWithFiles = await Promise.all(
        data?.map(async (apartment) => {
          const res = await getFiles({
            DataKey: "MHD_Appartment",
            DataValue: null,
            RecordId: null,
            RecordGuid: apartment?.id,
            ProjectTableId: 5,
          });
          let resFiles;
          if (res?.data?.[0]?.files?.[0]?.pages?.length > 0)
            resFiles = res?.data?.[0]?.files?.[0]?.pages;
          else resFiles = [];
          return {
            ...apartment,
            files: [...resFiles],
          };
        })
      );
      setData(dataWithFiles);
    }
  };

  useEffect(() => {
    form.setValue("appartments", null, { shouldDirty: false });
    form.setValue("appartments.created", [], { shouldDirty: false });
    form.setValue("appartments.updated", [], { shouldDirty: false });
    form.setValue("appartments.deleted", [], { shouldDirty: false });
    fetchFiles();
  }, [apartments]);

  const sortTable = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    let sortedData;
    if (["ageRange", "gender", "isAmea"].includes(key)) {
      sortedData = [...data].sort((a, b) => {
        const aValue = a[key]?.name || "";
        const bValue = b[key]?.name || "";
        if (aValue < bValue) {
          return direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    } else {
      sortedData = [...data].sort((a, b) => {
        if (a[key] < b[key]) {
          return direction === "ascending" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    setData(sortedData);
  };

  const getIcon = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        return <i className="fas fa-sort-up"></i>;
      } else {
        return <i className="fas fa-sort-down"></i>;
      }
    } else {
      return <i className="fas fa-sort"></i>;
    }
  };

  const handleAddRow = async (apartment, files) => {
    if (!apartment.floor) apartment.floor = null;
    else apartment.floor = parseInt(apartment.floor);
    if (!apartment.m2) apartment.m2 = null;
    else apartment.m2 = parseInt(apartment.m2);

    const bundleId = uuidv4();
    const formData = new FormData();
    formData.append("Form Description", "Files");

    for (let index = 0; index < files?.length; index++) {
      const file = files[index];
      formData.append(`Files[${index}].FileName`, file.name);
      formData.append(`Files[${index}].BundleId`, bundleId);
      formData.append(`Files[${index}].FileTypeId`, apartment.fileType);
      formData.append(`Files[${index}].RowGuid`, file.rowGuid);
      formData.append(`Files[${index}].File`, file);
      file.inDb = true;
    }
    const res = await uploadFiles(formData);
    for (let index = 0; index < files?.length; index++) {
      const file = files[index];
      const match = res?.data?.find(
        (dbFile) => dbFile?.rowGuid === file?.rowGuid
      );
      file.id = match.id;
    }
    if (res?.status === "success" && res?.data?.length > 0)
      apartment.bundles = [bundleId];
    apartment.files = files;

    const existingApartments = form.getValues("apartments") ?? [];
    apartmentForm.setValue("apartments", [...existingApartments, apartment]);
    setData((prev) => [...prev, { ...apartment, position: prev?.length + 1 }]);
    setOpenDialog(false);
    apartmentForm.reset({});
    const createdApartments = form.getValues("appartments.created") ?? [];
    form.setValue("appartments.created", [...createdApartments, apartment], {
      shouldDirty: true,
    });
  };

  const handleBeginEdit = (apartment) => {
    if (apartment?.ownershipType?.id) apartmentForm.reset(apartment);
    else
      apartmentForm.reset({
        ...apartment,
        ownershipType: { id: 0 },
      });
    setOpenDialog(true);
  };

  const handleDeleteRow = (apartment) => {
    if (
      window.confirm(
        `Είστε σίγουροι οτι θέλετε να διαγράψετε το Διαμέρισμα #${apartment?.position}`
      )
    ) {
      if (apartment?.isNew) {
        const createdAppartments = form.getValues("appartments.created");
        form.setValue(
          "appartments.created",
          createdAppartments.filter((ap) => ap.id !== apartment.id),
          { shouldDirty: true }
        );
      } else {
        const updatedAppartments = form.getValues("appartments.updated");
        form.setValue(
          "appartments.updated",
          updatedAppartments.filter((ap) => ap.id !== apartment.id),
          { shouldDirty: true }
        );
        const deletedApartments = form.getValues("appartments.deleted") ?? [];
        form.setValue(
          "appartments.deleted",
          [...deletedApartments, apartment?.id],
          { shouldDirty: true }
        );
      }
      setData((prev) => {
        let returnData = prev;
        returnData = prev.filter((row) => row.id !== apartment.id);
        return returnData.map((row) =>
          row?.position > apartment?.position
            ? { ...row, position: row?.position - 1 }
            : row
        );
      });
    }
  };

  const handleEditRow = async (apartment, files) => {
    if (!apartment.floor) apartment.floor = null;
    else apartment.floor = parseInt(apartment.floor);
    if (!apartment.m2) apartment.m2 = null;
    else apartment.m2 = parseInt(apartment.m2);
    const formData = new FormData();
    formData.append("Form Description", "Files");

    for (let index = 0; index < files?.length; index++) {
      const file = files[index];
      if (!file?.id && !file?.inDb) {
        if (!apartment.bundleId) apartment.bundleId = uuidv4();

        formData.append(`Files[${index}].FileName`, file.name);
        formData.append(`Files[${index}].BundleId`, apartment.bundleId);
        formData.append(`Files[${index}].FileTypeId`, apartment.fileType);
        formData.append(`Files[${index}].RowGuid`, file.rowGuid);
        formData.append(`Files[${index}].File`, file);
        file.inDb = true;
      }
    }
    const res = await uploadFiles(formData);
    for (let index = 0; index < files?.length; index++) {
      const file = files[index];
      const match = res?.data?.find(
        (dbFile) => dbFile?.rowGuid === file?.rowGuid
      );
      if (!file.id) file.id = match?.id;
    }
    if (res?.status === "success" && res?.data?.length > 0)
      apartment.bundles = [apartment.bundleId];
    apartment.files = files;
    setData((prev) =>
      prev.map((row) =>
        row.id === apartment.id ? { ...row, ...apartment } : row
      )
    );
    const editedApartments = form
      .getValues("appartments.updated")
      .filter((ap) => ap.id !== apartment.id);

    if (apartment?.isNew) {
      const createdApartments =
        form
          .getValues("appartments.created")
          ?.filter((ap) => ap.id !== apartment.id) ?? [];
      form.setValue("appartments.created", [...createdApartments, apartment], {
        shouldDirty: true,
      });
    } else
      form.setValue("appartments.updated", [...editedApartments, apartment], {
        shouldDirty: true,
      });
    setOpenDialog(false);
    apartmentForm.reset({});
  };

  const handlePreviewFile = async (file, newFile = false) => {
    if (newFile) setFileToPreview(file);
    else {
      const res = await downloadFile(file?.id, file?.type ?? file?.contentType);
      setFileToPreview(res);
    }
  };

  const handleDeleteFile = async (delFile, id, bundleId) => {
    const res = await deleteFile({
      fileId: delFile?.id,
      bundleId,
      recordGuid: id ?? null,
      projectTableId: 5,
    });
    const createdAppartments = form.getValues("appartments.created");
    const updatedAppartments = form.getValues("appartments.updated");

    form.setValue(
      "appartments.created",
      createdAppartments.map((ap) =>
        ap.id === id ? { ...ap, bundles: [] } : ap
      ),
      { shouldDirty: true }
    );
    form.setValue(
      "appartments.updated",
      updatedAppartments.map((ap) =>
        ap.id === id ? { ...ap, bundles: [] } : ap
      ),
      { shouldDirty: true }
    );
    setData((prevData) =>
      prevData.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            files: item.files.filter((file) => file.id !== delFile?.id),
          };
        }
        return item;
      })
    );
    return res?.status;
  };

  return (
    <>
      {fileToPreview && (
        <FilePreviewer
          file={fileToPreview}
          onClose={() => setFileToPreview(null)}
        />
      )}
      <ApartmentFormDialog
        canEdit={canEdit}
        canPartiallyEdit={canPartiallyEdit}
        open={openDialog}
        handleAddRow={handleAddRow}
        handleClose={handleCloseDialog}
        apartmentForm={apartmentForm}
        handleEditRow={handleEditRow}
        handlePreviewFile={handlePreviewFile}
        handleDeleteFile={handleDeleteFile}
      />

      {data?.length < 5 && canAdd && (
        <Grid container item xs={12} sx={{ paddingBottom: 2 }}>
          <Button
            sx={{ marginLeft: "auto" }}
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              apartmentForm.reset();
              const findLookupObject = (lookupTable, value, fieldName) => {
                const selectedObject = lookupTable?.find(
                  (record) => record?.id === value
                );
                apartmentForm.setValue(fieldName, selectedObject.name);
              };
              if (branch?.regionId) {
                apartmentForm.setValue("regionId", branch?.regionId);
                findLookupObject(lookups.regions, branch?.regionId, "region");
              }
              if (branch?.prefectureId) {
                findLookupObject(
                  lookups.prefectures,
                  branch?.prefectureId,
                  "prefecture"
                );
                apartmentForm.setValue("prefectureId", branch?.prefectureId);
              }
              if (branch?.municipalityId) {
                apartmentForm.setValue(
                  "municipalityId",
                  branch?.municipalityId
                );
                findLookupObject(
                  lookups.municipalities,
                  branch?.municipalityId,
                  "municipality"
                );
              }
              setOpenDialog(true);
            }}
          >
            Προσθήκη
          </Button>
        </Grid>
      )}
      <div style={{ width: "100%", overflowX: "auto" }}>
        <table className="application-input-table">
          <thead>
            <tr>
              <th align="center" onClick={() => sortTable("position")}>
                Α/Α {getIcon("position")}
              </th>
              <th onClick={() => sortTable("prefecture")}>
                Περ.Ενότητα {getIcon("prefecture")}
              </th>
              <th onClick={() => sortTable("address")}>
                Διεύθυνση {getIcon("address")}
              </th>
              <th align="center" onClick={() => sortTable("gender")}>
                Φύλο {getIcon("gender")}
              </th>
              <th align="center" onClick={() => sortTable("ageRange")}>
                Ηλικίες {getIcon("ageRange")}
              </th>
              <th align="center" onClick={() => sortTable("isAmea")}>
                ΑμεΑ {getIcon("isAmea")}
              </th>
              <th align="center" onClick={() => sortTable("capacity")}>
                Δυναμικ. {getIcon("capacity")}
              </th>
              <th>Σύμβαση</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data?.map((row, index) => (
              <tr key={index}>
                <td align="center">{row?.position}</td>
                <td>{row?.prefecture}</td>
                <td>{row?.address}</td>
                <td align="center">{row?.gender?.name}</td>
                <td align="center">{row?.ageRange?.name}</td>
                <td align="center">{row?.isAmea?.name}</td>
                <td align="center">{row?.capacity}</td>
                <td align="center">
                  {row?.files?.map((file, index) => (
                    <p
                      key={index}
                      onClick={() => {
                        handlePreviewFile(file, !file?.id);
                      }}
                      style={{
                        color: "#387de7",
                        textDecoration: "underline",
                        cursor: "pointer",
                        marginTop: 3,
                      }}
                    >
                      {index + 1}
                      <AttachFile sx={{ fontSize: 20 }} />
                    </p>
                  ))}
                </td>
                <td>
                  <div className="action-buttons">
                    {(canEdit || canPartiallyEdit) && (
                      <button
                        onClick={() => handleBeginEdit(row)}
                        className="btn btn-edit-small"
                      >
                        <Edit fontSize="small" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteRow(row)}
                        className="btn btn-edit-small"
                      >
                        <Delete fontSize="small" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ApartmentsTable;
